import mime from 'mime';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '@Helpers';

import { sendErrorEvent } from '@Analytics';
import { MasterUploadRequest } from '@Models';
import { bopsApi } from '@Network';

export default function useMasterUpload() {
  const { t } = useTranslation();

  const uploadToAWS = async (uploadUrl: string, selectedFile: File, contentType: string) => {
    const requestOptions = {
      method: 'PUT',
      headers: { 'content-type': contentType },
      body: selectedFile,
    };

    const response = await fetch(uploadUrl, requestOptions);
    if (response.status !== 200) {
      const textResponse = await response.text();
      showErrorToast(t('Configuration.NewMasterError'));
      sendErrorEvent(`unable to upload file to AWS S3. ${textResponse}`);
      throw new Error(`Invalid response: ${response.status}`);
    }
  };

  const uploadFile = (file: File, newMasterType: string, title: string, connector: string) => {
    const uploadAsync = async () => {
      const contentType = mime.getType(file?.name) || 'text/csv';

      try {
        const uploadResponse = await bopsApi.requestUploadConfigurationFile(
          newMasterType.toUpperCase(),
          contentType,
          file?.name?.split('.').pop() || '',
          null,
          connector,
          'new_master',
        );

        const { data } = uploadResponse;

        return await uploadToAWS(data.presigned_url, file, contentType)
          .then(async () => {
            await bopsApi.postNotification(data.notification_message);
          })
          .catch((e) => {
            sendErrorEvent(`unable to upload file to AWS S3. ${e}`);
            throw new Error(`Invalid response`);
          });
      } catch (error: any) {
        let errorMessage = '';

        if (error && (error.response || error.responseText)) {
          errorMessage = error.response?.data?.detail || error.responseText;
        }
        throw new Error(errorMessage || t('Configuration.NewMasterError'));
      }
    };

    return uploadAsync();
  };

  const uploadFileToOneDrive = (
    file: File,
    fileId: string,
    fileName: string,
    connector: string,
    event: 'new_master' | 'update_master',
  ) => {
    const uploadAsync = async () => {
      try {
        const uploadRequest: MasterUploadRequest = {
          file,
          data: {
            file_name: fileName,
            file_id: fileId,
            connector,
            event,
          },
        };

        const response = await bopsApi.uploadMasterFile(uploadRequest);
        return response;
      } catch (error: any) {
        let errorMessage = '';

        if (error && (error.response || error.responseText)) {
          errorMessage = error.response?.data?.detail || error.responseText;

          if (errorMessage && errorMessage.includes('resourceLocked'))
            throw new Error(t('Configuration.ResourceLockedError'));
        }

        throw new Error(errorMessage || t('Configuration.NewMasterError'));
      }
    };

    return uploadAsync();
  };

  return { uploadFile, uploadFileToOneDrive };
}
